import React, { useState, useMemo } from "react";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Trash, Plus, Copy, Check, RotateCcw } from "lucide-react";

// HELPER: Simple curl parser that extracts details to build fetch/axios templates
interface ParsedCurl {
  url: string;
  method: string;
  headers: Record<string, string>;
  data: string;
}

function parseCurlCommand(rawCurl: string): ParsedCurl {
  const result: ParsedCurl = {
    url: "https://api.example.com/v1/data",
    method: "GET",
    headers: {},
    data: "",
  };

  if (!rawCurl) return result;

  // Clean raw curl string
  const clean = rawCurl.trim().replace(/\\\s*\n/g, " ");

  // Extract URL - first word starting with http or after -X/-H if not matched, or general URL detection
  const urlMatch = clean.match(/(?:https?:\/\/[^\s'"]+)/);
  if (urlMatch) {
    result.url = urlMatch[0];
  }

  // Extract Method
  const methodMatch = clean.match(/(?:-X|--request)\s+([A-Z]+)/i);
  if (methodMatch) {
    result.method = methodMatch[1].toUpperCase();
  } else if (clean.includes("-d") || clean.includes("--data") || clean.includes("--data-raw") || clean.includes("--data-binary") || clean.includes("--data-urlencode")) {
    result.method = "POST";
  }

  // Extract Headers
  const headerRegex = /(?:-H|--header)\s+["']([^"']+)["']/g;
  let headerMatch;
  while ((headerMatch = headerRegex.exec(clean)) !== null) {
    const parts = headerMatch[1].split(":");
    if (parts.length >= 2) {
      const key = parts[0].trim();
      const val = parts.slice(1).join(":").trim();
      result.headers[key] = val;
    }
  }

  // Extract Body Data
  const dataRegex = /(?:-d|--data|--data-raw|--data-binary|--data-urlencode)\s+["']([^"']+)["']/;
  const bodyMatch = clean.match(dataRegex);
  if (bodyMatch) {
    result.data = bodyMatch[1];
  } else {
    // try to match double quotes or other quote formats
    const dataRegex2 = /(?:-d|--data|--data-raw|--data-binary|--data-urlencode)\s+([^\s]+)/;
    const bodyMatch2 = clean.match(dataRegex2);
    if (bodyMatch2 && !bodyMatch2[1].startsWith("-")) {
      result.data = bodyMatch2[1].replace(/^["']|["']$/g, "");
    }
  }

  return result;
}

// 1. CURL BUILDER
export function CurlBuilder() {
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("https://api.example.com/v1/users");
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([
    { key: "Content-Type", value: "application/json" },
    { key: "Authorization", value: "Bearer YOUR_ACCESS_TOKEN" },
  ]);
  const [queryParams, setQueryParams] = useState<{ key: string; value: string }[]>([]);
  const [bodyType, setBodyType] = useState("json");
  const [rawBody, setRawBody] = useState('{\n  "name": "Jane Doe",\n  "email": "jane@example.com"\n}');
  const [options, setOptions] = useState({
    silent: false,
    verbose: false,
    insecure: false,
    followRedirects: false,
  });
  const [copied, setCopied] = useState(false);

  const finalUrl = useMemo(() => {
    try {
      const parsedUrl = new URL(url);
      queryParams.forEach((p) => {
        if (p.key) parsedUrl.searchParams.set(p.key, p.value);
      });
      return parsedUrl.toString();
    } catch {
      // Fallback if URL is incomplete
      if (queryParams.length > 0) {
        const queryStr = queryParams
          .filter((p) => p.key)
          .map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
          .join("&");
        return url + (url.includes("?") ? "&" : "?") + queryStr;
      }
      return url;
    }
  }, [url, queryParams]);

  const curlCommand = useMemo(() => {
    let cmd = `curl -X ${method}`;

    // Options
    if (options.silent) cmd += " -s";
    if (options.verbose) cmd += " -v";
    if (options.insecure) cmd += " -k";
    if (options.followRedirects) cmd += " -L";

    // Headers
    headers.forEach((h) => {
      if (h.key) {
        cmd += ` \\\n  -H "${h.key}: ${h.value}"`;
      }
    });

    // Body
    if (method !== "GET" && method !== "HEAD") {
      if (bodyType === "json" && rawBody) {
        cmd += ` \\\n  -d '${rawBody.replace(/\n/g, "").replace(/\s+/g, " ")}'`;
      } else if (rawBody) {
        cmd += ` \\\n  -d '${rawBody}'`;
      }
    }

    // URL at the end
    cmd += ` \\\n  "${finalUrl}"`;

    return cmd;
  }, [method, finalUrl, headers, bodyType, rawBody, options]);

  const handleCopy = () => {
    navigator.clipboard.writeText(curlCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const addHeader = () => setHeaders([...headers, { key: "", value: "" }]);
  const removeHeader = (index: number) => setHeaders(headers.filter((_, i) => i !== index));
  const updateHeader = (index: number, field: "key" | "value", val: string) => {
    const list = [...headers];
    list[index][field] = val;
    setHeaders(list);
  };

  const addQuery = () => setQueryParams([...queryParams, { key: "", value: "" }]);
  const removeQuery = (index: number) => setQueryParams(queryParams.filter((_, i) => i !== index));
  const updateQuery = (index: number, field: "key" | "value", val: string) => {
    const list = [...queryParams];
    list[index][field] = val;
    setQueryParams(list);
  };

  const handlePreset = (type: string) => {
    if (type === "get") {
      setMethod("GET");
      setUrl("https://api.github.com/users/octocat");
      setHeaders([
        { key: "Accept", value: "application/vnd.github+json" },
        { key: "User-Agent", value: "Awesome-Octocat-App" },
      ]);
      setQueryParams([{ key: "per_page", value: "10" }]);
    } else if (type === "post") {
      setMethod("POST");
      setUrl("https://api.example.com/v1/posts");
      setHeaders([
        { key: "Content-Type", value: "application/json" },
        { key: "Authorization", value: "Bearer api_key_123456" },
      ]);
      setRawBody('{\n  "title": "My Awesome Post",\n  "content": "Hello, World!"\n}');
      setQueryParams([]);
    }
  };

  return (
    <ToolPageLayout
      title="Free Online cURL Command Builder & Generator"
      description="Create customized, shell-ready cURL commands visually. Support for multiple HTTP methods, headers, body types, cookies, and network parameters."
      h1="cURL Builder"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Choose an HTTP method (GET, POST, PUT, DELETE, etc.) and enter your destination URL.",
        "Add custom headers and query parameters using the structured key-value builder fields.",
        "If building a write request, enter your JSON, form-data, or raw text body.",
        "Copy your generated command block and run it directly in your terminal."
      ]}
      faq={[
        { question: "What is cURL used for in web development?", answer: "cURL is a command-line utility used to send request payloads and pull server files or data using various internet protocols (mainly HTTP/HTTPS)." },
        { question: "How can I build a POST request with headers in cURL?", answer: "Choose POST as your method, add your request URL, add a header like 'Content-Type: application/json', and fill in the JSON body. Our builder automates the backslash line breaks." },
        { question: "Does this cURL generator save my sensitive APIs?", answer: "No. All configurations run exclusively on your device. We do not store or transmit any URL keys or headers." },
        { question: "How to add query parameters to a cURL URL?", answer: "Use our dedicated query fields to safely encode and stitch keys and values onto the tail of your URL automatically." },
        { question: "How do I ignore SSL certificate warnings in cURL?", answer: "Check the 'Insecure / Ignore SSL Certificate Errors' option to automatically append the -k flag." },
        { question: "What is the difference between -d and -H in cURL?", answer: "-d specifies the request data payload, while -H represents a request metadata header." },
        { question: "Can I generate cURL commands for REST API testing?", answer: "Yes! This builder is perfect for quickly drafting requests to APIs like GitHub, Stripe, or any local server routes." },
        { question: "How do I configure cURL to follow redirects?", answer: "Check the 'Follow Redirects' checkbox to include the -L option." },
        { question: "How do I format multiline cURL commands on Windows?", answer: "Our generator prints standard UNIX backslashes. For Windows command prompt, you can easily replace them with carets (^)." },
        { question: "What does the silent -s flag do in cURL?", answer: "It runs cURL in silent or quiet mode, disabling the progress meter and error outputs from printing to the terminal console." }
      ]}
    >
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Target API Setup</h3>
            <div className="flex gap-3">
              <div className="w-1/4">
                <Label>Method</Label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full mt-1.5 p-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {["GET", "POST", "PUT", "DELETE", "PATCH", "HEAD", "OPTIONS"].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <Label>Destination URL</Label>
                <Input
                  className="mt-1.5"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://api.example.com/v1/endpoint"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handlePreset("get")}
                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium transition-colors"
              >
                Preset: Github API (GET)
              </button>
              <button
                onClick={() => handlePreset("post")}
                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg font-medium transition-colors"
              >
                Preset: Auth Create (POST)
              </button>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-bold text-slate-900 text-sm">Request Headers</h3>
              <button
                onClick={addHeader}
                className="text-xs text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add Header
              </button>
            </div>
            {headers.length === 0 ? (
              <p className="text-xs text-slate-400">No custom headers configured.</p>
            ) : (
              <div className="space-y-2">
                {headers.map((h, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      placeholder="Header Name"
                      value={h.key}
                      onChange={(e) => updateHeader(i, "key", e.target.value)}
                      className="flex-1 text-xs"
                    />
                    <Input
                      placeholder="Value"
                      value={h.value}
                      onChange={(e) => updateHeader(i, "value", e.target.value)}
                      className="flex-1 text-xs"
                    />
                    <button
                      onClick={() => removeHeader(i)}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-bold text-slate-900 text-sm">Query Parameters</h3>
              <button
                onClick={addQuery}
                className="text-xs text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add Query Parameter
              </button>
            </div>
            {queryParams.length === 0 ? (
              <p className="text-xs text-slate-400">No query parameters configured.</p>
            ) : (
              <div className="space-y-2">
                {queryParams.map((q, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      placeholder="Key"
                      value={q.key}
                      onChange={(e) => updateQuery(i, "key", e.target.value)}
                      className="flex-1 text-xs"
                    />
                    <Input
                      placeholder="Value"
                      value={q.value}
                      onChange={(e) => updateQuery(i, "value", e.target.value)}
                      className="flex-1 text-xs"
                    />
                    <button
                      onClick={() => removeQuery(i)}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {method !== "GET" && method !== "HEAD" && (
            <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
              <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Request Body</h3>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="bodyType"
                    checked={bodyType === "json"}
                    onChange={() => setBodyType("json")}
                    className="text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  JSON Payload
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="bodyType"
                    checked={bodyType === "raw"}
                    onChange={() => setBodyType("raw")}
                    className="text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  Raw/Plain Text
                </label>
              </div>
              <Textarea
                value={rawBody}
                onChange={(e) => setRawBody(e.target.value)}
                rows={5}
                className="font-mono text-xs"
                placeholder={bodyType === "json" ? '{\n  "key": "value"\n}' : "Your plain text body..."}
              />
            </div>
          )}

          <div className="p-5 bg-white border border-slate-200 rounded-xl space-y-4">
            <h3 className="font-bold text-slate-900 text-sm border-b pb-2">Advanced Flags</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.silent}
                  onChange={(e) => setOptions({ ...options, silent: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                Silent / Quiet (-s)
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.verbose}
                  onChange={(e) => setOptions({ ...options, verbose: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                Verbose Output (-v)
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.insecure}
                  onChange={(e) => setOptions({ ...options, insecure: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                Insecure / Ignore SSL (-k)
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.followRedirects}
                  onChange={(e) => setOptions({ ...options, followRedirects: e.target.checked })}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                Follow Redirects (-L)
              </label>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <div className="sticky top-4 space-y-4">
            <div className="p-5 bg-slate-900 text-white rounded-xl space-y-4 shadow-xl">
              <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">cURL Output</span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors bg-slate-800 px-3 py-1.5 rounded-lg"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-400" /> Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy Code
                    </>
                  )}
                </button>
              </div>
              <pre className="font-mono text-xs overflow-x-auto leading-relaxed text-slate-100 max-h-[400px] whitespace-pre-wrap break-all">
                {curlCommand}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

// 2. CURL TO FETCH CONVERTER
export function CurlToFetch() {
  const [curlInput, setCurlInput] = useState(
    `curl -X POST "https://api.example.com/v1/users" \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Bearer my_secret_token" \\\n  -d '{"name": "Jane", "role": "admin"}'`
  );
  const [copied, setCopied] = useState(false);

  const fetchCode = useMemo(() => {
    const parsed = parseCurlCommand(curlInput);

    let code = `// Formatted Native Fetch API Request\n`;
    code += `async function sendRequest() {\n`;
    code += `  const url = '${parsed.url}';\n`;
    code += `  const options = {\n`;
    code += `    method: '${parsed.method}',\n`;

    // Headers
    if (Object.keys(parsed.headers).length > 0) {
      code += `    headers: {\n`;
      Object.entries(parsed.headers).forEach(([key, val]) => {
        code += `      '${key}': '${val}',\n`;
      });
      code += `    },\n`;
    }

    // Body
    if (parsed.data) {
      try {
        // format JSON
        const obj = JSON.parse(parsed.data);
        code += `    body: JSON.stringify(${JSON.stringify(obj, null, 6).trim()}),\n`;
      } catch {
        code += `    body: '${parsed.data}',\n`;
      }
    }

    code += `  };\n\n`;
    code += `  try {\n`;
    code += `    const response = await fetch(url, options);\n`;
    code += `    if (!response.ok) {\n`;
    code += `      throw new Error(\`HTTP error! status: \${response.status}\`);\n`;
    code += `    }\n`;
    code += `    const data = await response.json();\n`;
    code += `    console.log(data);\n`;
    code += `    return data;\n`;
    code += `  } catch (error) {\n`;
    code += `    console.error('Request failed:', error);\n`;
    code += `  }\n`;
    code += `}\n`;

    return code;
  }, [curlInput]);

  const handleCopy = () => {
    navigator.clipboard.writeText(fetchCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => setCurlInput("");

  return (
    <ToolPageLayout
      title="Free Online cURL to Fetch API Code Converter"
      description="Instantly parse, translate, and convert shell cURL scripts into standard ES6 Fetch API javascript/typescript code blocks online."
      h1="cURL to Fetch Converter"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Paste your raw terminal cURL script into the input box.",
        "The tool automatically parses headers, authentication, method, and payload on the fly.",
        "Copy the generated, clean modern async/await Fetch code and drop it into your frontend code."
      ]}
      faq={[
        { question: "How can I convert cURL to standard JS Fetch?", answer: "Paste your cURL script into the input panel. Our parser extracts the target endpoint, request method, and headers to construct clean fetch code blocks." },
        { question: "Does this fetch converter support JSON body payloads?", answer: "Yes. If the cURL body contains JSON, the code generator embeds standard JSON.stringify formatting automatically." },
        { question: "Is Fetch API supported in Node.js?", answer: "Yes, Node.js 18 and newer versions provide built-in native Fetch support without needing external npm modules." },
        { question: "Why convert cURL requests to Fetch?", answer: "Front-end JS code uses the Fetch API natively, whereas cURL is primarily used in terminal command line scripts." },
        { question: "How does the tool handle headers like authorization?", answer: "The parser detects any -H parameters and appends them cleanly as keys inside the options.headers dictionary." },
        { question: "Does this cURL to Fetch tool run server-side?", answer: "No. The parsing code runs purely client-side inside your browser sandbox, keeping keys and tokens totally safe." },
        { question: "Can I convert cURL commands containing -X GET?", answer: "Yes, the parser detects -X GET, POST, PUT, DELETE, and handles parameters accurately." },
        { question: "Does it support nested JSON formatting?", answer: "Yes, we attempt to pretty-print nested JSON parameters using modern indentation spacing rules." },
        { question: "Can I use the output in TypeScript?", answer: "Yes, the async function output structure conforms perfectly to both standard JS and TypeScript configurations." },
        { question: "How to handle request error states in Fetch?", answer: "The generator includes a standard try-catch block wrapping a response.ok conditional check to catch network failures safely." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-slate-900 font-bold">Paste cURL Command</Label>
            <button
              onClick={handleClear}
              className="text-xs text-rose-500 hover:text-rose-600 flex items-center gap-1 font-semibold"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
          <Textarea
            value={curlInput}
            onChange={(e) => setCurlInput(e.target.value)}
            rows={10}
            className="font-mono text-xs bg-slate-50"
            placeholder="curl -X GET https://api.example.com/v1/status"
          />
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-slate-900 font-bold">JavaScript Fetch Output</Label>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> Copy Code
                </>
              )}
            </button>
          </div>
          <pre className="p-4 rounded-xl border border-slate-200 font-mono text-xs overflow-x-auto bg-slate-900 text-slate-100 min-h-[220px]">
            <code>{fetchCode}</code>
          </pre>
        </div>
      </div>
    </ToolPageLayout>
  );
}

// 3. CURL TO AXIOS CONVERTER
export function CurlToAxios() {
  const [curlInput, setCurlInput] = useState(
    `curl -X PUT "https://api.example.com/v1/users/42" \\\n  -H "Accept: application/json" \\\n  -H "Content-Type: application/json" \\\n  -d '{"status": "active"}'`
  );
  const [copied, setCopied] = useState(false);

  const axiosCode = useMemo(() => {
    const parsed = parseCurlCommand(curlInput);

    let code = `// Formatted Axios HTTP Request\n`;
    code += `import axios from 'axios';\n\n`;
    code += `async function sendRequest() {\n`;
    code += `  const config = {\n`;
    code += `    method: '${parsed.method.toLowerCase()}',\n`;
    code += `    url: '${parsed.url}',\n`;

    // Headers
    if (Object.keys(parsed.headers).length > 0) {
      code += `    headers: {\n`;
      Object.entries(parsed.headers).forEach(([key, val]) => {
        code += `      '${key}': '${val}',\n`;
      });
      code += `    },\n`;
    }

    // Body data
    if (parsed.data) {
      try {
        const obj = JSON.parse(parsed.data);
        code += `    data: ${JSON.stringify(obj, null, 6).trim()},\n`;
      } catch {
        code += `    data: '${parsed.data}',\n`;
      }
    }

    code += `  };\n\n`;
    code += `  try {\n`;
    code += `    const response = await axios(config);\n`;
    code += `    console.log(response.data);\n`;
    code += `    return response.data;\n`;
    code += `  } catch (error) {\n`;
    code += `    if (error.response) {\n`;
    code += `      // Server responded with non-2xx status\n`;
    code += `      console.error('Error status:', error.response.status);\n`;
    code += `    } else {\n`;
    code += `      console.error('Network Error:', error.message);\n`;
    code += `    }\n`;
    code += `  }\n`;
    code += `}\n`;

    return code;
  }, [curlInput]);

  const handleCopy = () => {
    navigator.clipboard.writeText(axiosCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => setCurlInput("");

  return (
    <ToolPageLayout
      title="Free Online cURL to Axios Code Converter"
      description="Translate shell cURL strings into perfectly indented Axios client requests. Fast, browser-based generator for frontend and backend Javascript."
      h1="cURL to Axios Converter"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Enter or paste any cURL script containing URL, header key-values, or payload block.",
        "The tool converts your terminal code into clean, ready-to-run axios configurations instantly.",
        "Copy your configured axios request function to paste directly into your project codebase."
      ]}
      faq={[
        { question: "How do I convert a cURL request to Axios?", answer: "Input your cURL terminal code inside the text console, and our converter instantly constructs an optimized Axios config block." },
        { question: "What is Axios in JavaScript?", answer: "Axios is a popular promise-based HTTP client for the browser and node.js, helping simplify headers, request payloads, and response JSON formats." },
        { question: "Is this cURL to Axios converter free to use?", answer: "Yes! This utility is 100% free and processes all strings inside your browser without logging requests on our servers." },
        { question: "Does it parse Authorization Bearer tokens from cURL?", answer: "Yes, headers containing authorization strings are parsed and formatted cleanly under the configuration block." },
        { question: "How does Axios handle request body payloads compared to fetch?", answer: "Axios handles JSON serialization automatically under the 'data' parameter, eliminating the need to type JSON.stringify()." },
        { question: "Can I convert cURL scripts for file uploads?", answer: "Yes, it parses standard payload blocks, although complex multi-part configurations may require additional JS FormData setup." },
        { question: "Are cookies supported in the output Axios configs?", answer: "Yes, you can easily append options like 'withCredentials: true' to your generated configuration block if needed." },
        { question: "How does it print headers with dashes?", answer: "Our converter wraps all header keys (such as 'Content-Type') inside single quotes to avoid Javascript syntax errors." },
        { question: "Does the output function support async/await?", answer: "Yes, the converted output uses modern, readable ES6 async/await syntax wrapped in a robust try-catch handler." },
        { question: "What is the advantage of Axios over native Fetch?", answer: "Axios provides handy defaults, simpler error boundaries, request cancellation, and automatic JSON transformations out of the box." }
      ]}
    >
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-slate-900 font-bold">Paste cURL Command</Label>
            <button
              onClick={handleClear}
              className="text-xs text-rose-500 hover:text-rose-600 flex items-center gap-1 font-semibold"
            >
              <RotateCcw className="w-3 h-3" /> Clear
            </button>
          </div>
          <Textarea
            value={curlInput}
            onChange={(e) => setCurlInput(e.target.value)}
            rows={10}
            className="font-mono text-xs bg-slate-50"
            placeholder={"curl -X POST https://api.example.com/v1/posts -d '{\"key\":\"value\"}'"}
          />
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-slate-900 font-bold">Axios JS Output</Label>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> Copy Code
                </>
              )}
            </button>
          </div>
          <pre className="p-4 rounded-xl border border-slate-200 font-mono text-xs overflow-x-auto bg-slate-900 text-slate-100 min-h-[220px]">
            <code>{axiosCode}</code>
          </pre>
        </div>
      </div>
    </ToolPageLayout>
  );
}
